import BigNumber from "bignumber.js";
import { ActionCableService, distinctBigNumberUntilChanged, sharePublishReplay, swalloError } from "ngx-libs";
import { combineLatest, from, Observable, timer } from "rxjs";
import { debounceTime, map, mergeMap, startWith, switchMap, take, tap } from "rxjs/operators";
import { keepUpdater } from "../rxjs";
import SpotPricerAbi from "src/app/abi/spot-pricer";
import { AccountService } from "../services/account.service";
import { ContractService } from "../services/contract.service";
import { EthWalletService } from "../services/eth-wallet.service";
import { Base } from "./base";
import { DECIMALS18, Market } from "./market";
import { environment } from "src/environments/environment";

export class SpotPricer extends Base {
  price: Observable<BigNumber>;

  constructor(
    protected contract: string,
    protected accountService: AccountService,
    protected contractService: ContractService,
    private ethWalletService: EthWalletService,
    private actionCableService: ActionCableService,
    private market: Market
  ) {
    super(contract, SpotPricerAbi, accountService,contractService);

    const received = this.actionCableService.received({
      channel: 'SpotPriceChannel'
    }).pipe(
      startWith(0),
      debounceTime(10)
    );

    this.price = combineLatest([received, timer(0, 60000)]).pipe(
      debounceTime(20),
      keepUpdater(),
      switchMap(() => this.getPrice().pipe(swalloError())),
      distinctBigNumberUntilChanged(),
      tap((v) => {
        this.market.contractVault.spotPricer.price = v.toDecimal();
      }),
      sharePublishReplay(1)
    );
  }

  setPrice(price: any) {
    return  combineLatest([this.instances.instance, this.accountService.account]).pipe(
      take(1),
      mergeMap(([i, a]) => from(i.methods.setPrice(new BigNumber(price).times(DECIMALS18).toString(10)).send({
        from: a
      })))
    );
  }

  setSettledPrice(expiry: number, price: any) {
    return  combineLatest([this.instances.instance, this.accountService.account]).pipe(
      take(1),
      mergeMap(([i, a]) => from(i.methods.setSettledPrice(expiry, new BigNumber(price).times(DECIMALS18).toString(10)).send({
        from: a
      })))
    );
  }

  getPrice() {
    if (environment.features.apiSpot) {
      return this.market.api.oracles.getSpotPrice().pipe(
        map((json) => new BigNumber(json.spotPrice).fromDecimal())
      );
    } else {
      return this.call((i) => i.methods.getPrice()).pipe(
        map((v: any) => new BigNumber(v).fromDecimal())
      );
    }
  }

  getSettledPrice(expiry: number) {
    return this.call((i) => i.methods.settledPrices(expiry)).pipe(
      map((v: any) => new BigNumber(v).fromDecimal())
    );
  }
}
